import {
  useDeleteAgreementMutation,
  useProjectId,
  useUpdateAgreementMutation,
} from '@consigli/hooks';
import { type MeetingAgreement } from '@consigli/types';
import { useCallback } from 'react';

import { useMsalAuth } from './use-msal-auth';

interface UpdateAgreementBody {
  agreement?: string;
  responsibleParties?: Array<{
    meetingUserId?: string | null;
    customPartyName?: string | null;
  }>;
}

export const useAgreementMutations = (meetingId: string) => {
  const projectId = useProjectId();
  const { acquireToken, userEmail, userId } = useMsalAuth();

  const [_updateAgreement] = useUpdateAgreementMutation();
  const [_deleteAgreement, { isLoading: isDeleting }] = useDeleteAgreementMutation();

  const updateAgreement = useCallback(
    async (agreementId: string, body: UpdateAgreementBody): Promise<MeetingAgreement> => {
      const token = await acquireToken();
      return _updateAgreement({
        projectId,
        meetingId,
        agreementId,
        msAccessToken: token ?? '',
        userEmail,
        userId,
        body,
      }).unwrap();
    },
    [_updateAgreement, acquireToken, meetingId, projectId, userEmail, userId],
  );

  const deleteAgreement = useCallback(
    async (agreementId: string): Promise<void> => {
      const token = await acquireToken();
      await _deleteAgreement({
        projectId,
        meetingId,
        agreementId,
        msAccessToken: token ?? '',
        userEmail,
        userId,
      }).unwrap();
    },
    [_deleteAgreement, acquireToken, meetingId, projectId, userEmail, userId],
  );

  return { updateAgreement, deleteAgreement, isDeleting };
};
